import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Trash2, Pencil, Tag, Loader2, X, Upload, Star, CornerDownRight } from 'lucide-react';
import { toursChartersApi, uploadTourImage } from './toursChartersApi';
import { toast } from '../../../hooks/use-toast';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../../components/ui/sheet';

const inputCls = 'w-full rounded-lg border border-white/10 bg-[#061a1f] px-3 py-2 text-sm text-white placeholder-white/30 outline-none focus:border-teal-500/50';
const labelCls = 'mb-1.5 block text-xs font-medium text-white/60';

const blankCategory = {
  name: '', slug: '', description: '', subtitle: '', image_url: '', sort_order: 0,
  is_favorite: false, parent_id: '', landing_path: '', legacy_path: '',
};

const ActivityCategories = () => {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    toursChartersApi.listCategories().then((r) => setCats(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  /** Top level categories in order, each followed by its own sub-categories. */
  const tree = useMemo(() => {
    const tops = cats.filter((c) => !c.parent_id);
    return tops.map((top) => ({ ...top, children: cats.filter((c) => c.parent_id === top.id) }));
  }, [cats]);

  /** Everything that may be picked as a parent: no self, no own descendants. */
  const parentOptions = useMemo(() => {
    if (!modal?.id) return cats.filter((c) => !c.parent_id);
    const banned = new Set([modal.id]);
    let grew = true;
    while (grew) {
      grew = false;
      cats.forEach((c) => {
        if (c.parent_id && banned.has(c.parent_id) && !banned.has(c.id)) { banned.add(c.id); grew = true; }
      });
    }
    return cats.filter((c) => !banned.has(c.id));
  }, [cats, modal]);

  const uploadImg = async (file) => {
    if (!file) return;
    setUploading(true);
    try { const url = await uploadTourImage(file); setModal((m) => ({ ...m, image_url: url })); }
    catch { toast({ title: 'Upload failed', variant: 'destructive' }); }
    finally { setUploading(false); }
  };

  const handleImageDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) uploadImg(file);
  };

  const save = async () => {
    if (!modal.name?.trim()) { toast({ title: 'Name required', variant: 'destructive' }); return; }
    setSaving(true);
    const payload = {
      name: modal.name,
      slug: modal.slug || '',
      description: modal.description || '',
      subtitle: modal.subtitle || '',
      image_url: modal.image_url || '',
      sort_order: Number(modal.sort_order) || 0,
      is_favorite: !!modal.is_favorite,
      parent_id: modal.parent_id || '',
      landing_path: modal.landing_path || '',
      legacy_path: modal.legacy_path || '',
    };
    try {
      if (modal.id) await toursChartersApi.updateCategory(modal.id, payload);
      else await toursChartersApi.createCategory(payload);
      toast({ title: modal.id ? 'Category updated' : 'Category created' });
      setModal(null); load();
    } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail, variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  /** Star / unstar a category - favourites are what the home page features. */
  const toggleFavorite = async (cat) => {
    const next = !cat.is_favorite;
    setCats((prev) => prev.map((c) => (c.id === cat.id ? { ...c, is_favorite: next } : c)));
    try {
      await toursChartersApi.updateCategory(cat.id, { is_favorite: next });
      toast({ title: next ? `${cat.name} added to home page` : `${cat.name} removed from home page` });
    } catch (e) {
      setCats((prev) => prev.map((c) => (c.id === cat.id ? { ...c, is_favorite: !next } : c)));
      toast({ title: 'Could not update favourite', variant: 'destructive' });
    }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Delete category "${name}"? Activities using it will simply lose this tag.`)) return;
    try {
      await toursChartersApi.deleteCategory(id);
      toast({ title: 'Deleted' });
      load();
    } catch (e) {
      toast({ title: 'Could not delete', description: e.response?.data?.detail, variant: 'destructive' });
    }
  };

  const row = (c, isChild) => (
    <div
      key={c.id}
      className={`flex items-center justify-between rounded-xl border border-white/10 bg-[#0b1f24] p-4 ${isChild ? 'ml-6 border-l-2 border-l-teal-500/40' : ''}`}
      data-testid={`category-${c.id}`}
    >
      <div className="flex min-w-0 items-center gap-3">
        {isChild && <CornerDownRight className="h-4 w-4 shrink-0 text-teal-400/60" />}
        {c.image_url
          ? <img src={c.image_url} alt={c.name} className="h-10 w-10 rounded-lg object-cover" />
          : <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-teal-500/20"><Tag className="h-4 w-4 text-teal-300" /></span>}
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-2 font-semibold">
            {c.name}
            <span className="rounded bg-white/10 px-1.5 py-0.5 text-[10px] font-bold text-white/50">#{c.sort_order ?? 0}</span>
            {c.activity_count > 0 && (
              <span className="rounded bg-teal-500/15 px-1.5 py-0.5 text-[10px] font-bold text-teal-300">{c.activity_count} activities</span>
            )}
            {c.landing_path && (
              <span className="rounded bg-blue-500/15 px-1.5 py-0.5 text-[10px] font-bold text-blue-300" title="Landing page URL">/{c.landing_path}</span>
            )}
          </p>
          <p className="truncate text-xs text-white/40">/{c.slug}{c.description ? ` \u00b7 ${c.description}` : ''}</p>
        </div>
      </div>
      <div className="flex shrink-0 gap-1">
        <button
          onClick={() => toggleFavorite(c)}
          className={`rounded-lg p-2 transition-colors ${c.is_favorite ? 'text-amber-300 hover:bg-amber-400/10' : 'text-white/30 hover:bg-white/10 hover:text-white/60'}`}
          title={c.is_favorite ? 'Starred - showing on the home page' : 'Star to feature on the home page'}
          aria-pressed={!!c.is_favorite}
          data-testid={`category-favorite-${c.id}`}
        >
          <Star className={`h-4 w-4 ${c.is_favorite ? 'fill-current' : ''}`} />
        </button>
        <button onClick={() => setModal({ ...blankCategory, ...c, parent_id: c.parent_id || '' })} className="rounded-lg p-2 text-white/60 hover:bg-white/10" data-testid={`category-edit-${c.id}`}><Pencil className="h-4 w-4" /></button>
        <button onClick={() => del(c.id, c.name)} className="rounded-lg p-2 text-red-300 hover:bg-red-500/10" data-testid={`category-delete-${c.id}`}><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  );

  return (
    <div className="-m-4 min-h-screen bg-[#061a1f] p-5 text-white lg:-m-6 lg:p-8" data-testid="activity-categories-page">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black">Activity Categories</h1>
          <p className="text-sm text-white/40">
            {cats.filter((c) => !c.parent_id).length} categories &middot; {cats.filter((c) => c.parent_id).length} sub-categories &middot; {cats.filter((c) => c.is_favorite).length} starred for the home page
          </p>
        </div>
        <button onClick={() => setModal({ ...blankCategory })} className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 px-4 py-2.5 text-sm font-semibold" data-testid="add-category-btn">
          <Plus className="h-4 w-4" /> Add Category
        </button>
      </div>

      {loading ? <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-teal-400" /></div>
        : cats.length === 0 ? <div className="rounded-2xl border border-dashed border-white/10 bg-[#0b1f24] py-16 text-center text-white/40" data-testid="categories-empty">No categories yet.</div>
        : (
          <div className="space-y-2">
            {tree.map((top) => (
              <div key={top.id} className="space-y-2">
                {row(top, false)}
                {top.children.map((child) => row(child, true))}
              </div>
            ))}
          </div>
        )}

      {modal && (
        <Sheet open={!!modal} onOpenChange={(open) => !open && setModal(null)}>
          <SheetContent side="right" className="w-full sm:max-w-md bg-[#0b1f24] border-white/10 text-white overflow-y-auto" data-testid="category-modal">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2 text-white"><Tag className="h-5 w-5 text-teal-300" /> {modal.id ? 'Edit' : 'New'} Category</SheetTitle>
            </SheetHeader>
            <div className="mt-4 space-y-3 pb-10">
              <input className={inputCls} placeholder="Category name (e.g. Boat Charters)" value={modal.name} onChange={(e) => setModal({ ...modal, name: e.target.value })} data-testid="category-name-input" />

              <div>
                <label className={labelCls}>Parent Category (leave empty for a main category)</label>
                <select
                  className={inputCls}
                  value={modal.parent_id || ''}
                  onChange={(e) => setModal({ ...modal, parent_id: e.target.value })}
                  data-testid="category-parent-select"
                >
                  <option value="">&mdash; No parent (main category) &mdash;</option>
                  {parentOptions.map((p) => (
                    <option key={p.id} value={p.id}>{p.parent_name ? `${p.parent_name} \u203a ${p.name}` : p.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelCls}>Page URL slug &mdash; keep the legacy URL so indexed pages are not lost</label>
                <input className={inputCls} placeholder="booking-private-powerboat-charters-in-st-john-vi" value={modal.slug || ''} onChange={(e) => setModal({ ...modal, slug: e.target.value })} data-testid="category-slug-input" />
                <p className="mt-1 text-[11px] text-white/35">Reachable at /{modal.slug || 'slug'} and /activities/category/{modal.slug || 'slug'}</p>
              </div>

              <input className={inputCls} placeholder="Description (optional)" value={modal.description || ''} onChange={(e) => setModal({ ...modal, description: e.target.value })} data-testid="category-description-input" />
              <input className={inputCls} placeholder="Subtitle shown under the landing page title" value={modal.subtitle || ''} onChange={(e) => setModal({ ...modal, subtitle: e.target.value })} data-testid="category-subtitle-input" />

              <div>
                <label className={labelCls}>Landing page URL (porthole tiles of its sub-categories)</label>
                <input className={inputCls} placeholder="tours-charters-st-john-virgin-islands/private-boat-charters" value={modal.landing_path || ''} onChange={(e) => setModal({ ...modal, landing_path: e.target.value })} data-testid="category-landing-path-input" />
              </div>

              <div>
                <label className={labelCls}>Extra legacy URL alias (optional)</label>
                <input className={inputCls} placeholder="old-url-that-should-still-work" value={modal.legacy_path || ''} onChange={(e) => setModal({ ...modal, legacy_path: e.target.value })} data-testid="category-legacy-path-input" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Order Number</label>
                  <input
                    type="number"
                    className={inputCls}
                    value={modal.sort_order ?? 0}
                    onChange={(e) => setModal({ ...modal, sort_order: Number(e.target.value) || 0 })}
                    data-testid="category-order-input"
                  />
                </div>
                <div>
                  <label className={labelCls}>Home Page</label>
                  <button
                    type="button"
                    onClick={() => setModal({ ...modal, is_favorite: !modal.is_favorite })}
                    className={`flex w-full items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${
                      modal.is_favorite
                        ? 'border-amber-400/40 bg-amber-400/10 text-amber-300'
                        : 'border-white/10 bg-[#061a1f] text-white/50 hover:text-white/80'
                    }`}
                    aria-pressed={!!modal.is_favorite}
                    data-testid="category-favorite-toggle"
                  >
                    <Star className={`h-4 w-4 ${modal.is_favorite ? 'fill-current' : ''}`} />
                    {modal.is_favorite ? 'Featured' : 'Not featured'}
                  </button>
                </div>
              </div>

              <div>
                <label className={labelCls}>Category Image (shown on the public directory / porthole tile)</label>
                <div className="flex items-center gap-3">
                  {modal.image_url ? (
                    <div className="relative">
                      <img src={modal.image_url} alt="" className="h-16 w-24 rounded-lg object-cover" />
                      <button onClick={() => setModal({ ...modal, image_url: '' })} className="absolute -right-2 -top-2 rounded-full bg-black p-0.5 text-red-300"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  ) : (
                    <div
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={handleImageDrop}
                      onClick={() => fileRef.current?.click()}
                      className="flex h-16 w-24 cursor-pointer flex-col items-center justify-center gap-0.5 rounded-lg border border-dashed border-white/20 text-white/40 hover:border-teal-400/50 hover:text-white/60"
                      data-testid="category-image-upload"
                    >
                      {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4" /><span className="text-[9px]">Drop or click</span></>}
                    </div>
                  )}
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImg(e.target.files?.[0])} />
                </div>
              </div>
              <button onClick={save} disabled={saving} className="w-full rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 py-2.5 text-sm font-semibold disabled:opacity-50" data-testid="category-save-btn">
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
};

export default ActivityCategories;
